import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState, useCallback, memo } from "react";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import CustomInput from "../../components/inputs/CustomInputs";
import { fetchRawMaterialApi } from "../../apis/rawMaterial.api";
import { DAY_WEEK_MONTH, VENDOR_PRIORITY } from "../../utils/constants";
import { findNameByRole, titleCase } from "../../utils/main";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";

// Memoized individual material row component
const MaterialRow = memo(({
  val,
  inx,
  CheckBox,
  fields,
  setFields,
  loading,
  onDelete,
  onChange,
}) => {
  return (
    <Box mt={3} display={"flex"} width={"100%"} key={val.id || inx} gap={2}>
      {CheckBox.type === "Raw_Materials" && CheckBox.IsActive && (
        <Checkbox
          onChange={() => onChange(inx)}
          inputProps={{ "aria-label": "controlled" }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          justifyContent: "flex-end",
          width: "65%",
        }}
      >
        <AsyncDropDown
          disableClearable
          titleKey="name"
          Value={(() => {
            const rawValue = fields.rawMaterials[inx]?.rawMaterialId;
            const id =
              rawValue && typeof rawValue === "object"
                ? rawValue._id
                : rawValue;
            if (!id || typeof id !== "string") return null;

            const name =
              rawValue && typeof rawValue === "object"
                ? rawValue?.name
                : fields.rawMaterials[inx]?.displayName;

            return {
              _id: id,
              name:
                rawValue && typeof rawValue === "object" && rawValue?.mpn
                  ? `${name} [${rawValue?.mpn}]`
                  : name || "",
            };
          })()}
          lazyFun={async (para) =>
            await fetchRawMaterialApi({ ...para, allStatus: true })
          }
          OptionComponent={({ option, ...rest }) => (
            <ListItem {...rest}>
              {option.name} {`[${option.mpn}]`}
            </ListItem>
          )}
          onChange={(changedVal) => {
            setFields((prevFields) => {
              const updatedRawMaterials = [...prevFields.rawMaterials];
              updatedRawMaterials[inx] = {
                ...updatedRawMaterials[inx],
                rawMaterialId: changedVal ? changedVal._id : null,
                displayName: changedVal ? changedVal.name : "",
              };
              return { ...prevFields, rawMaterials: updatedRawMaterials };
            });
          }}
          label={"Select Material"}
        />
      </Box>

      <Box sx={{ width: "20%" }}>
        <CustomInput
          disabled={loading}
          value={fields?.rawMaterials[inx]?.min_of_quantity || ""}
          onChange={(e) => {
            setFields((prevFields) => {
              const updatedRawMaterials = [...prevFields.rawMaterials];
              updatedRawMaterials[inx] = {
                ...updatedRawMaterials[inx],
                min_of_quantity: e.target.value,
              };
              return { ...prevFields, rawMaterials: updatedRawMaterials };
            });
          }}
          type="number"
          label={"Qty"}
        />
      </Box>

      <Box sx={{ width: "15%" }}>
        <CustomInput
          disabled={loading}
          value={fields?.rawMaterials[inx]?.lead_time || ""}
          onChange={(e) => {
            setFields((prevFields) => {
              const updatedRawMaterials = [...prevFields.rawMaterials];
              updatedRawMaterials[inx] = {
                ...updatedRawMaterials[inx],
                lead_time: e.target.value,
              };
              return { ...prevFields, rawMaterials: updatedRawMaterials };
            });
          }}
          type="text"
          label={"Lead Time"}
        />
      </Box>

      <Box sx={{ width: "15%" }}>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            disabled={loading}
            label="Priority"
            value={fields?.rawMaterials[inx]?.priority || ""}
            onChange={(e) => {
              setFields((prevFields) => {
                const updatedRawMaterials = [...prevFields.rawMaterials];
                updatedRawMaterials[inx] = {
                  ...updatedRawMaterials[inx],
                  priority: e.target.value,
                };
                return { ...prevFields, rawMaterials: updatedRawMaterials };
              });
            }}
          >
            {Object.entries(VENDOR_PRIORITY).map(([key, val], index) => (
              <MenuItem key={index} value={val}>
                {titleCase(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "5%",
        }}
      >
        <IconButton
          disabled={loading}
          size="small"
          onClick={() => onDelete(inx)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo
  return (
    prevProps.inx === nextProps.inx &&
    prevProps.CheckBox.type === nextProps.CheckBox.type &&
    prevProps.CheckBox.IsActive === nextProps.CheckBox.IsActive &&
    prevProps.loading === nextProps.loading &&
    prevProps.val === nextProps.val &&
    prevProps.fields.rawMaterials[prevProps.inx] === nextProps.fields.rawMaterials[nextProps.inx]
  );
});

MaterialRow.displayName = "MaterialRow";

const AddMaterialUi = ({ fields, setFields, loading, CheckBox }) => {
  const ITEMS_PER_PAGE = 20; // Show 20 items per page
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(
    () => Math.ceil((fields?.rawMaterials?.length || 0) / ITEMS_PER_PAGE),
    [fields?.rawMaterials?.length]
  );

  const paginatedMaterials = useMemo(() => {
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    return fields?.rawMaterials?.slice(
      startIdx,
      startIdx + ITEMS_PER_PAGE
    ) || [];
  }, [fields?.rawMaterials, currentPage]);

  const handleDeleteItem = useCallback(
    (index) => {
      const updatedItems = fields.rawMaterials.filter((_, inx) => inx !== index);
      setFields((prev) => ({ ...prev, err: "", rawMaterials: updatedItems }));
      // Adjust page if needed
      if (updatedItems.length > 0 && Math.ceil(updatedItems.length / ITEMS_PER_PAGE) < currentPage) {
        setCurrentPage(Math.ceil(updatedItems.length / ITEMS_PER_PAGE));
      }
    },
    [fields.rawMaterials, setFields, currentPage]
  );

  const handleChange = useCallback(
    (index) => {
      setFields((prevFields) => {
        const updatedRawMaterials = [...prevFields.rawMaterials];
        updatedRawMaterials[index] = {
          ...updatedRawMaterials[index],
          link: "",
          IsLinkActive: updatedRawMaterials[index]?.IsLinkActive ? false : true,
        };
        return { ...prevFields, rawMaterials: updatedRawMaterials };
      });
    },
    [setFields]
  );

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
    // Scroll to top of list
    setTimeout(() => {
      const element = document.getElementById("materials-list-container");
      if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, []);

  return (
    <Box>
      <Box
        id="materials-list-container"
        sx={{
          maxHeight: "600px",
          overflowY: "auto",
          mb: 2,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {paginatedMaterials.map((val, idx) => {
          const actualIndex = (currentPage - 1) * ITEMS_PER_PAGE + idx;
          if (!fields.rawMaterials[actualIndex]) return null;

          // Ensure stable id
          if (!val.id) {
            val.id = uuidv4();
          }

          return (
            <MaterialRow
              key={val.id}
              val={val}
              inx={actualIndex}
              CheckBox={CheckBox}
              fields={fields}
              setFields={setFields}
              loading={loading}
              onDelete={handleDeleteItem}
              onChange={handleChange}
            />
          );
        })}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mt: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2">
            Total: {fields?.rawMaterials?.length || 0} materials | Page {currentPage} of {totalPages}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            size="small"
          />
        </Box>
      )}

      {/* Info message */}
      {(fields?.rawMaterials?.length || 0) > ITEMS_PER_PAGE && (
        <Typography variant="caption" sx={{ display: "block", mt: 1, textAlign: "center", color: "gray" }}>
          Showing {paginatedMaterials.length} of {fields?.rawMaterials?.length || 0} items
        </Typography>
      )}
    </Box>
  );
};

export default memo(AddMaterialUi);
